/**
 * MySuggestionsPage
 *
 * Full page for users to view and manage their own suggestions.
 * Users can see all suggestions they've submitted, their statuses,
 * and dismiss pending suggestions they created.
 *
 * Part of Story 30: Domain Knowledge Refinement System
 *
 * Date: December 20, 2025
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import {
  MessageSquare,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  CheckCircle,
  Loader2,
  RefreshCw,
  ArrowLeft,
  Lightbulb,
  Bug,
  FileText,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';
import { useNavigate } from 'react-router-dom';

interface Suggestion {
  id: string;
  suggestionType: string;
  title: string;
  description: string;
  status: string;
  resolvedByName?: string;
  resolutionNotes?: string;
  createdAt: string;
  updatedAt?: string;
}

const statusConfig: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  pending: {
    icon: <Clock className="h-4 w-4" />,
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    label: 'Pending'
  },
  reviewed: {
    icon: <CheckCircle className="h-4 w-4" />,
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    label: 'Reviewed'
  },
  implemented: {
    icon: <CheckCircle2 className="h-4 w-4" />,
    color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    label: 'Implemented'
  },
  dismissed: {
    icon: <XCircle className="h-4 w-4" />,
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
    label: 'Dismissed'
  },
};

const typeConfig: Record<string, { icon: React.ReactNode; label: string }> = {
  SEND_SUGGESTION: {
    icon: <Lightbulb className="h-4 w-4" />,
    label: 'Suggestion'
  },
  FLAG_BUG: {
    icon: <Bug className="h-4 w-4" />,
    label: 'Bug Report'
  },
  CREATE_DATA_ISSUE: {
    icon: <FileText className="h-4 w-4" />,
    label: 'Data Issue'
  },
};

export default function MySuggestionsPage() {
  const { selectedSite } = useSite();
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dismissDialogOpen, setDismissDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);
  const [dismissReason, setDismissReason] = useState('');
  const [dismissing, setDismissing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  useEffect(() => {
    if (selectedSite?.id) {
      fetchSuggestions();
    }
  }, [selectedSite?.id]);

  const fetchSuggestions = async () => {
    if (!selectedSite?.id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(`/content-evaluations/my-suggestions?siteId=${selectedSite.id}`);
      const data = await response.json();
      if (data.success) {
        setSuggestions(data.data || []);
      } else {
        setError('Failed to load suggestions');
      }
    } catch (err) {
      console.error('[MySuggestionsPage] Error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async () => {
    if (!selectedSuggestion) return;

    setDismissing(true);
    try {
      const response = await authFetch(`/content-evaluations/suggestions/${selectedSuggestion.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'dismissed',
          resolutionNotes: dismissReason || 'Dismissed by reporter',
        }),
      });

      const data = await response.json();
      if (data.success) {
        toast({
          title: 'Suggestion Dismissed',
          description: 'Your suggestion has been dismissed.',
        });
        setDismissDialogOpen(false);
        setSelectedSuggestion(null);
        setDismissReason('');
        fetchSuggestions();
      } else {
        toast({
          title: 'Error',
          description: data.error || 'Failed to dismiss suggestion',
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.error('[MySuggestionsPage] Error dismissing:', err);
      toast({
        title: 'Error',
        description: 'Failed to dismiss suggestion',
        variant: 'destructive',
      });
    } finally {
      setDismissing(false);
    }
  };

  const openDismissDialog = (suggestion: Suggestion) => {
    setSelectedSuggestion(suggestion);
    setDismissDialogOpen(true);
  };

  const openDetailDialog = (suggestion: Suggestion) => {
    setSelectedSuggestion(suggestion);
    setDetailDialogOpen(true);
  };

  const filteredSuggestions = statusFilter
    ? suggestions.filter(s => s.status === statusFilter)
    : suggestions;

  const statusCounts = suggestions.reduce((acc, s) => {
    acc[s.status] = (acc[s.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  if (!selectedSite?.id) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              Please select a site from the header to view your suggestions.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="mb-2">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <MessageSquare className="h-8 w-8" />
            My Suggestions
          </h1>
          <p className="text-muted-foreground mt-1">
            Track the status of suggestions and feedback you've submitted
          </p>
        </div>
        <Button variant="outline" onClick={fetchSuggestions} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card
          className={`cursor-pointer transition-all ${statusFilter === null ? 'ring-2 ring-primary' : 'hover:bg-accent'}`}
          onClick={() => setStatusFilter(null)}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{suggestions.length}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-muted-foreground opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${statusFilter === 'pending' ? 'ring-2 ring-primary' : 'hover:bg-accent'}`}
          onClick={() => setStatusFilter(statusFilter === 'pending' ? null : 'pending')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">{statusCounts.pending || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${statusFilter === 'implemented' ? 'ring-2 ring-primary' : 'hover:bg-accent'}`}
          onClick={() => setStatusFilter(statusFilter === 'implemented' ? null : 'implemented')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Implemented</p>
                <p className="text-2xl font-bold">{statusCounts.implemented || 0}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${statusFilter === 'dismissed' ? 'ring-2 ring-primary' : 'hover:bg-accent'}`}
          onClick={() => setStatusFilter(statusFilter === 'dismissed' ? null : 'dismissed')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Dismissed</p>
                <p className="text-2xl font-bold">{statusCounts.dismissed || 0}</p>
              </div>
              <XCircle className="h-8 w-8 text-gray-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Suggestions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Submitted Suggestions
            <Badge variant="secondary">{filteredSuggestions.length}</Badge>
          </CardTitle>
          <CardDescription>
            View and manage suggestions you've submitted for platform improvements
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4 p-4 border rounded-lg">
                  <Skeleton className="h-8 w-8 rounded" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-3 w-2/3" />
                  </div>
                  <Skeleton className="h-6 w-20" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-destructive mb-4" />
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button variant="outline" onClick={fetchSuggestions}>
                Try Again
              </Button>
            </div>
          ) : filteredSuggestions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {statusFilter
                  ? `No ${statusFilter} suggestions found.`
                  : 'No suggestions submitted yet.'}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Suggestions can be submitted during content review.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSuggestions.map((suggestion) => {
                  const status = statusConfig[suggestion.status] || statusConfig.pending;
                  const type = typeConfig[suggestion.suggestionType] || {
                    icon: <MessageSquare className="h-4 w-4" />,
                    label: suggestion.suggestionType
                  };
                  return (
                    <TableRow key={suggestion.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {type.icon}
                          <span className="text-sm">{type.label}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <button
                          className="text-left hover:underline"
                          onClick={() => openDetailDialog(suggestion)}
                        >
                          <p className="font-medium">{suggestion.title}</p>
                          <p className="text-xs text-muted-foreground line-clamp-1">
                            {suggestion.description}
                          </p>
                        </button>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${status.color} gap-1`}>
                          {status.icon}
                          {status.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatDistanceToNow(new Date(suggestion.createdAt), { addSuffix: true })}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDetailDialog(suggestion)}
                          >
                            View
                          </Button>
                          {suggestion.status === 'pending' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => openDismissDialog(suggestion)}
                            >
                              Dismiss
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedSuggestion && typeConfig[selectedSuggestion.suggestionType]?.icon}
              {selectedSuggestion?.title}
            </DialogTitle>
            <DialogDescription>
              Submitted {selectedSuggestion && formatDistanceToNow(new Date(selectedSuggestion.createdAt), { addSuffix: true })}
            </DialogDescription>
          </DialogHeader>

          {selectedSuggestion && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Status</Label>
                <div className="mt-1">
                  <Badge className={`${statusConfig[selectedSuggestion.status]?.color} gap-1`}>
                    {statusConfig[selectedSuggestion.status]?.icon}
                    {statusConfig[selectedSuggestion.status]?.label}
                  </Badge>
                </div>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Description</Label>
                <p className="mt-1 text-sm bg-secondary p-3 rounded-md">
                  {selectedSuggestion.description}
                </p>
              </div>

              {selectedSuggestion.resolutionNotes && (
                <div>
                  <Label className="text-xs text-muted-foreground">Resolution Notes</Label>
                  <p className="mt-1 text-sm bg-green-50 dark:bg-green-950 p-3 rounded-md text-green-800 dark:text-green-200">
                    {selectedSuggestion.resolutionNotes}
                  </p>
                </div>
              )}

              {selectedSuggestion.resolvedByName && (
                <div>
                  <Label className="text-xs text-muted-foreground">Resolved By</Label>
                  <p className="mt-1 text-sm">{selectedSuggestion.resolvedByName}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
              Close
            </Button>
            {selectedSuggestion?.status === 'pending' && (
              <Button
                variant="destructive"
                onClick={() => {
                  setDetailDialogOpen(false);
                  openDismissDialog(selectedSuggestion);
                }}
              >
                Dismiss Suggestion
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dismiss Dialog */}
      <Dialog open={dismissDialogOpen} onOpenChange={setDismissDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dismiss Suggestion</DialogTitle>
            <DialogDescription>
              Are you sure you want to dismiss this suggestion? This cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {selectedSuggestion && (
            <div className="py-2">
              <p className="text-sm font-medium">{selectedSuggestion.title}</p>
              <p className="text-xs text-muted-foreground mt-1">{selectedSuggestion.description}</p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="reason">Reason (optional)</Label>
            <Textarea
              id="reason"
              placeholder="Why are you dismissing this suggestion?"
              value={dismissReason}
              onChange={(e) => setDismissReason(e.target.value)}
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDismissDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDismiss}
              disabled={dismissing}
            >
              {dismissing && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Dismiss
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
